import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';

import { paths } from '../paths';
import DashboardLayout from '../../layouts/dashboard';
import { RouteFallback } from '../../ui/RouteFallback';
import { RoleRoute } from '../../components/RoleRoute';
import { HomeRedirect } from '../../components/HomeRedirect';
import { PanelLayout } from '../../layouts/panels/PanelLayout';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import InsightsLayout from '../../layouts/insights/InsightsLayout';
import {
  canManageCache,
  canViewBiosignal,
  canViewCustomers,
  canWorkCoachChat,
  canViewProduction,
  canWorkSupportChat,
  canAccessAdminShell,
  canRunSupportConsole,
  canViewCoachInsights,
  canManageAppVersions,
  canManageAssessments,
  canManageSleepCoaches,
  canManageActivityGroups,
} from '../../lib/roles';

// Import factories are shared by lazy() and preloadRoute() so hover-preloads prime the same chunks.
const loadAssetPage = () => import('../../pages/AssetPage');
const loadSessionAudioPage = () => import('../../pages/SessionAudioPage');
const loadTeamPage = () => import('../../pages/TeamPage');
const loadSecurityPage = () => import('../../pages/SecurityPage');
const loadReviewOverviewPage = () => import('../../pages/ReviewOverviewPage');
// One page for every single-type queue; the route supplies the reviewable type.
const loadReviewQueuePage = () => import('../../pages/ReviewQueuePage');
const loadBulkAudioUploadPage = () => import('../../pages/BulkAudioUploadPage');
const loadUnitsImportPage = () => import('../../pages/UnitsImportPage');
const loadAuditLogPage = () => import('../../pages/AuditLogPage');
const loadPlanActivityPage = () => import('../../pages/PlanActivityPage');
const loadActivityGroupsPage = () => import('../../pages/ActivityGroupsPage');
const loadAudioFormView = () => import('../../sections/audio/view/AudioFormView');
const loadActivityFormView = () => import('../../sections/plan/ActivityFormView');
const loadNotificationsPage = () => import('../../pages/NotificationsPage');
const loadNotificationStatsPage = () => import('../../pages/NotificationStatsPage');
const loadNotificationFrameworkPage = () => import('../../pages/NotificationFrameworkPage');
const loadNotificationFormView = () =>
  import('../../sections/notifications/view/NotificationFormView');
const loadPlacementTesterPage = () => import('../../pages/PlacementTesterPage');
const loadDeviceListPage = () => import('../../pages/DeviceListPage');
const loadDeviceDetailPage = () => import('../../pages/DeviceDetailPage');
const loadUserDeviceLinksPage = () => import('../../pages/UserDeviceLinksPage');
const loadFirmwareListPage = () => import('../../pages/FirmwareListPage');
const loadFirmwareDetailPage = () => import('../../pages/FirmwareDetailPage');
const loadFirmwareReviewQueuePage = () => import('../../pages/FirmwareReviewQueuePage');
const loadUpdateLogsPage = () => import('../../pages/UpdateLogsPage');
const loadRollbackListPage = () => import('../../pages/RollbackListPage');
const loadRollbackDetailPage = () => import('../../pages/RollbackDetailPage');
const loadEntitlementListPage = () => import('../../pages/EntitlementListPage');
const loadPlanListPage = () => import('../../pages/PlanListPage');
const loadAppVersionsPage = () => import('../../pages/AppVersionsPage');
const loadCacheManagerPage = () => import('../../pages/CacheManagerPage');
const loadTaxonomyPage = () => import('../../pages/TaxonomyPage');
const loadAttributesPage = () => import('../../pages/AttributesPage');
const loadSupportConsolePage = () => import('../../pages/SupportConsolePage');
const loadCoachConsolePage = () => import('../../pages/CoachConsolePage');
const loadQualityReviewPage = () => import('../../pages/QualityReviewPage');
const loadCaseloadHealthPage = () => import('../../pages/CaseloadHealthPage');
const loadCoachDirectoryPage = () => import('../../pages/CoachDirectoryPage');
const loadCoachProfilePage = () => import('../../pages/CoachProfilePage');
const loadMyProfilePage = () => import('../../pages/MyProfilePage');
const loadAssignmentQueuePage = () => import('../../pages/AssignmentQueuePage');
const loadOpsNowPage = () => import('../../pages/OpsNowPage');
const loadOpsTeamPage = () => import('../../pages/OpsTeamPage');
const loadOpsIssuesPage = () => import('../../pages/IssuesPage');
const loadOpsMacrosPage = () => import('../../pages/MacrosPage');
const loadOpsConversationsPage = () => import('../../pages/OpsConversationsPage');
const loadCustomerListPage = () => import('../../pages/CustomerListPage');
const loadCustomerDetailPage = () => import('../../pages/CustomerDetailPage');
const loadCustomerInsightsPage = () => import('../../pages/CustomerInsightsPage');
const loadInsightsPage = () => import('../../pages/InsightsPage');
const loadDeviceSyncsPage = () => import('../../pages/DeviceSyncsPage');
const loadNightMetricsPage = () => import('../../pages/NightMetricsPage');
const loadObservationsPage = () => import('../../pages/ObservationsPage');
const loadAssessmentsPage = () => import('../../pages/AssessmentsPage');
const loadAssessmentEditor = () => import('../../sections/assessments/AssessmentEditor');
const loadProductionLinePage = () => import('../../pages/ProductionLinePage');
const loadProductionDevicesPage = () => import('../../pages/ProductionDevicesPage');
const loadProductionDevicePage = () => import('../../pages/ProductionDevicePage');
const loadProductionChargersPage = () => import('../../pages/ProductionChargersPage');
const loadProductionChargerPage = () => import('../../pages/ProductionChargerPage');
const loadProductionSetsPage = () => import('../../pages/ProductionSetsPage');
const loadProductionQualityPage = () => import('../../pages/ProductionQualityPage');
const loadProductionBenchesPage = () => import('../../pages/ProductionBenchesPage');
const loadProductionAboutPage = () => import('../../pages/ProductionAboutPage');

const InsightsPage = lazy(loadInsightsPage);
const DeviceSyncsPage = lazy(loadDeviceSyncsPage);
const NightMetricsPage = lazy(loadNightMetricsPage);
const ObservationsPage = lazy(loadObservationsPage);
const AssetPage = lazy(loadAssetPage);
const SessionAudioPage = lazy(loadSessionAudioPage);
const TeamPage = lazy(loadTeamPage);
const SecurityPage = lazy(loadSecurityPage);
const ReviewOverviewPage = lazy(loadReviewOverviewPage);
const ReviewQueuePage = lazy(loadReviewQueuePage);
const BulkAudioUploadPage = lazy(loadBulkAudioUploadPage);
const UnitsImportPage = lazy(loadUnitsImportPage);
const AuditLogPage = lazy(loadAuditLogPage);
const PlanActivityPage = lazy(loadPlanActivityPage);
const ActivityGroupsPage = lazy(loadActivityGroupsPage);
const AudioFormView = lazy(loadAudioFormView);
const ActivityFormView = lazy(loadActivityFormView);
const NotificationsPage = lazy(loadNotificationsPage);
const NotificationStatsPage = lazy(loadNotificationStatsPage);
const NotificationFrameworkPage = lazy(loadNotificationFrameworkPage);
const NotificationFormView = lazy(loadNotificationFormView);
const PlacementTesterPage = lazy(loadPlacementTesterPage);
const DeviceListPage = lazy(loadDeviceListPage);
const DeviceDetailPage = lazy(loadDeviceDetailPage);
const UserDeviceLinksPage = lazy(loadUserDeviceLinksPage);
const FirmwareListPage = lazy(loadFirmwareListPage);
const FirmwareDetailPage = lazy(loadFirmwareDetailPage);
const FirmwareReviewQueuePage = lazy(loadFirmwareReviewQueuePage);
const UpdateLogsPage = lazy(loadUpdateLogsPage);
const RollbackListPage = lazy(loadRollbackListPage);
const RollbackDetailPage = lazy(loadRollbackDetailPage);
const EntitlementListPage = lazy(loadEntitlementListPage);
const PlanListPage = lazy(loadPlanListPage);
const AppVersionsPage = lazy(loadAppVersionsPage);
const CacheManagerPage = lazy(loadCacheManagerPage);
const TaxonomyPage = lazy(loadTaxonomyPage);
const AttributesPage = lazy(loadAttributesPage);
const SupportConsolePage = lazy(loadSupportConsolePage);
const CoachConsolePage = lazy(loadCoachConsolePage);
const QualityReviewPage = lazy(loadQualityReviewPage);
const CaseloadHealthPage = lazy(loadCaseloadHealthPage);
const CoachDirectoryPage = lazy(loadCoachDirectoryPage);
const CoachProfilePage = lazy(loadCoachProfilePage);
const MyProfilePage = lazy(loadMyProfilePage);
const AssignmentQueuePage = lazy(loadAssignmentQueuePage);
const OpsNowPage = lazy(loadOpsNowPage);
const OpsTeamPage = lazy(loadOpsTeamPage);
const OpsIssuesPage = lazy(loadOpsIssuesPage);
const OpsMacrosPage = lazy(loadOpsMacrosPage);
const OpsConversationsPage = lazy(loadOpsConversationsPage);
const CustomerListPage = lazy(loadCustomerListPage);
const CustomerDetailPage = lazy(loadCustomerDetailPage);
const CustomerInsightsPage = lazy(loadCustomerInsightsPage);
const AssessmentsPage = lazy(loadAssessmentsPage);
const AssessmentEditor = lazy(loadAssessmentEditor);
const ProductionLinePage = lazy(loadProductionLinePage);
const ProductionDevicesPage = lazy(loadProductionDevicesPage);
const ProductionDevicePage = lazy(loadProductionDevicePage);
const ProductionChargersPage = lazy(loadProductionChargersPage);
const ProductionChargerPage = lazy(loadProductionChargerPage);
const ProductionSetsPage = lazy(loadProductionSetsPage);
const ProductionQualityPage = lazy(loadProductionQualityPage);
const ProductionBenchesPage = lazy(loadProductionBenchesPage);
const ProductionAboutPage = lazy(loadProductionAboutPage);

const preloadByPath = {
  [paths.dashboard.team]: loadTeamPage,
  [paths.dashboard.security]: loadSecurityPage,
  [paths.dashboard.review]: loadReviewOverviewPage,
  [paths.dashboard.session.audio.queue]: loadReviewQueuePage,
  [paths.dashboard.session.audio.bulk]: loadBulkAudioUploadPage,
  [paths.dashboard.content.taxonomyQueue]: loadReviewQueuePage,
  [paths.dashboard.plan.activity.queue]: loadReviewQueuePage,
  [paths.dashboard.auditLog]: loadAuditLogPage,
  [paths.dashboard.notifications.root]: loadNotificationsPage,
  [paths.dashboard.notifications.framework.root]: loadNotificationFrameworkPage,
  [paths.dashboard.content.taxonomy]: loadTaxonomyPage,
  [paths.dashboard.content.attributes]: loadAttributesPage,
  [paths.dashboard.session.audio.root]: loadSessionAudioPage,
  [paths.dashboard.plan.activity.root]: loadPlanActivityPage,
  [paths.dashboard.plan.activity.groups]: loadActivityGroupsPage,
  [paths.dashboard.cv.placementTester]: loadPlacementTesterPage,
  [paths.dashboard.devices.root]: loadDeviceListPage,
  [paths.dashboard.devices.links]: loadUserDeviceLinksPage,
  [paths.dashboard.devices.import]: loadUnitsImportPage,
  [paths.dashboard.firmware.root]: loadFirmwareListPage,
  [paths.dashboard.firmware.queue]: loadFirmwareReviewQueuePage,
  [paths.dashboard.firmware.logs]: loadUpdateLogsPage,
  [paths.dashboard.firmware.rollbacks]: loadRollbackListPage,
  [paths.dashboard.subscriptions.entitlements]: loadEntitlementListPage,
  [paths.dashboard.subscriptions.plans]: loadPlanListPage,
  [paths.dashboard.appVersions]: loadAppVersionsPage,
  [paths.dashboard.cache]: loadCacheManagerPage,
  [paths.dashboard.support.console]: loadSupportConsolePage,
  [paths.dashboard.coach.console]: loadCoachConsolePage,
  [paths.dashboard.coach.quality]: loadQualityReviewPage,
  [paths.dashboard.coach.caseload]: loadCaseloadHealthPage,
  [paths.dashboard.coach.directory]: loadCoachDirectoryPage,
  [paths.dashboard.myProfile]: loadMyProfilePage,
  [paths.dashboard.coach.assignment]: loadAssignmentQueuePage,
  [paths.dashboard.ops.now]: loadOpsNowPage,
  [paths.dashboard.ops.team]: loadOpsTeamPage,
  [paths.dashboard.ops.issues]: loadOpsIssuesPage,
  [paths.dashboard.ops.macros]: loadOpsMacrosPage,
  [paths.dashboard.ops.conversations]: loadOpsConversationsPage,
  [paths.dashboard.customers.root]: loadCustomerListPage,
  [paths.dashboard.insights.root]: loadInsightsPage,
  [paths.dashboard.biosignal.root]: loadDeviceSyncsPage,
  [paths.dashboard.biosignal.metrics]: loadNightMetricsPage,
  [paths.dashboard.biosignal.observations]: loadObservationsPage,
  [paths.dashboard.assessments]: loadAssessmentsPage,
  [paths.dashboard.production.root]: loadProductionLinePage,
  [paths.dashboard.production.devices]: loadProductionDevicesPage,
  [paths.dashboard.production.chargers]: loadProductionChargersPage,
  [paths.dashboard.production.sets]: loadProductionSetsPage,
  [paths.dashboard.production.quality]: loadProductionQualityPage,
  [paths.dashboard.production.benches]: loadProductionBenchesPage,
  [paths.dashboard.production.about]: loadProductionAboutPage,
  [paths.dashboard.videos]: loadAssetPage,
  [paths.dashboard.images]: loadAssetPage,
  [paths.dashboard.audio]: loadAssetPage,
};

// Prime a route's chunk (e.g. on nav-link hover/focus); webpack caches the module.
export const preloadRoute = (path) => {
  preloadByPath[path]?.().catch(() => {});
};

// The boundary sits inside the layout so a page crash leaves the sidebar and topbar
// usable. Keyed on pathname so navigating away clears the error instead of sticking.
const DashboardLayoutWrapper = () => {
  const { pathname } = useLocation();
  return (
    <DashboardLayout>
      <ErrorBoundary key={pathname}>
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </DashboardLayout>
  );
};

// Insights replaces the admin shell with its own three-panel layout, so it mounts beside
// the dashboard route rather than inside it.
const InsightsLayoutWrapper = () => {
  const { pathname } = useLocation();
  return (
    <RoleRoute allow={canViewCoachInsights}>
      <InsightsLayout>
        <ErrorBoundary key={pathname}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </InsightsLayout>
    </RoleRoute>
  );
};

// Dedicated panels share URL space, but never mount the controlled-app sidebar.
const pageRoutes = [
  { path: 'team', element: <TeamPage /> },
  { path: 'profile', element: <MyProfilePage /> },
  { path: 'security', element: <SecurityPage /> },
  { path: 'review', element: <ReviewOverviewPage /> },
  // Old bookmark of the three-tab queue.
  { path: 'review-queue', element: <Navigate to="/dashboard/review" replace /> },
  { path: 'activity-log', element: <AuditLogPage /> },
  { path: 'notifications', element: <NotificationsPage /> },
  { path: 'notifications/new', element: <NotificationFormView /> },
  { path: 'notifications/framework', element: <NotificationFrameworkPage tab="summary" /> },
  {
    path: 'notifications/framework/ledger',
    element: <NotificationFrameworkPage tab="ledger" />,
  },
  {
    path: 'notifications/framework/registry',
    element: <NotificationFrameworkPage tab="registry" />,
  },
  { path: 'notifications/:id/stats', element: <NotificationStatsPage /> },
  { path: 'content/taxonomy', element: <TaxonomyPage /> },
  { path: 'content/taxonomy/queue', element: <ReviewQueuePage type="taxonomy_node" /> },
  { path: 'content/attributes', element: <AttributesPage /> },
  { path: 'session/audio', element: <SessionAudioPage /> },
  { path: 'session/audio/queue', element: <ReviewQueuePage type="audio" /> },
  { path: 'session/audio/bulk', element: <BulkAudioUploadPage /> },
  { path: 'session/audio/new', element: <AudioFormView /> },
  { path: 'session/audio/:id/edit', element: <AudioFormView /> },
  { path: 'plan/activity', element: <PlanActivityPage /> },
  { path: 'plan/activity/queue', element: <ReviewQueuePage type="activity" /> },
  {
    path: 'coach/activity-groups',
    element: (
      <RoleRoute allow={canManageActivityGroups}>
        <ActivityGroupsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'plan/activity/overrides',
    element: <Navigate to={paths.dashboard.plan.activity.groups} replace />,
  },
  { path: 'plan/activity/new', element: <ActivityFormView /> },
  { path: 'plan/activity/:id/edit', element: <ActivityFormView /> },
  { path: 'cv/placement-tester', element: <PlacementTesterPage /> },
  { path: 'devices', element: <DeviceListPage /> },
  { path: 'devices/links', element: <UserDeviceLinksPage /> },
  { path: 'devices/import', element: <UnitsImportPage /> },
  { path: 'devices/:id', element: <DeviceDetailPage /> },
  { path: 'firmware', element: <FirmwareListPage /> },
  { path: 'firmware/queue', element: <FirmwareReviewQueuePage /> },
  { path: 'firmware/logs', element: <UpdateLogsPage /> },
  { path: 'firmware/rollbacks', element: <RollbackListPage /> },
  { path: 'firmware/rollbacks/:id', element: <RollbackDetailPage /> },
  { path: 'firmware/:id', element: <FirmwareDetailPage /> },
  {
    path: 'biosignal',
    element: (
      <RoleRoute allow={canViewBiosignal}>
        <DeviceSyncsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'biosignal/metrics',
    element: (
      <RoleRoute allow={canViewBiosignal}>
        <NightMetricsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'biosignal/observations',
    element: (
      <RoleRoute allow={canViewBiosignal}>
        <ObservationsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'ops/now',
    element: (
      <RoleRoute allow={canRunSupportConsole}>
        <OpsNowPage />
      </RoleRoute>
    ),
  },
  {
    path: 'ops/team',
    element: (
      <RoleRoute allow={canRunSupportConsole}>
        <OpsTeamPage />
      </RoleRoute>
    ),
  },
  {
    path: 'ops/issues',
    element: (
      <RoleRoute allow={canRunSupportConsole}>
        <OpsIssuesPage />
      </RoleRoute>
    ),
  },
  {
    path: 'ops/macros',
    element: (
      <RoleRoute allow={canRunSupportConsole}>
        <OpsMacrosPage />
      </RoleRoute>
    ),
  },
  {
    path: 'ops/conversations',
    element: (
      <RoleRoute allow={canRunSupportConsole}>
        <OpsConversationsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'support/console',
    element: (
      <RoleRoute allow={canWorkSupportChat}>
        <SupportConsolePage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/caseload',
    element: (
      <RoleRoute allow={canManageSleepCoaches}>
        <CaseloadHealthPage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/coaches',
    element: (
      <RoleRoute allow={canManageSleepCoaches}>
        <CoachDirectoryPage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/coaches/:memberId',
    element: (
      <RoleRoute allow={canManageSleepCoaches}>
        <CoachProfilePage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/quality',
    element: (
      <RoleRoute allow={canManageSleepCoaches}>
        <QualityReviewPage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/console',
    element: (
      <RoleRoute allow={canWorkCoachChat}>
        <CoachConsolePage />
      </RoleRoute>
    ),
  },
  {
    path: 'coach/assignment',
    element: (
      <RoleRoute allow={canManageSleepCoaches}>
        <AssignmentQueuePage />
      </RoleRoute>
    ),
  },
  { path: 'subscriptions/entitlements', element: <EntitlementListPage /> },
  { path: 'subscriptions/plans', element: <PlanListPage /> },
  {
    path: 'app-versions',
    element: (
      <RoleRoute allow={canManageAppVersions}>
        <AppVersionsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'cache',
    element: (
      <RoleRoute allow={canManageCache}>
        <CacheManagerPage />
      </RoleRoute>
    ),
  },
  {
    path: 'assessments',
    element: (
      <RoleRoute allow={canManageAssessments}>
        <AssessmentsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'assessments/:slug',
    element: (
      <RoleRoute allow={canManageAssessments}>
        <AssessmentEditor />
      </RoleRoute>
    ),
  },
  {
    path: 'customers',
    element: (
      <RoleRoute allow={canViewCustomers}>
        <CustomerListPage />
      </RoleRoute>
    ),
  },
  {
    path: 'customers/:id',
    element: (
      <RoleRoute allow={canViewCustomers}>
        <CustomerDetailPage />
      </RoleRoute>
    ),
  },
  {
    path: 'customers/:id/insights',
    element: (
      <RoleRoute allow={canViewCustomers}>
        <CustomerInsightsPage />
      </RoleRoute>
    ),
  },
  // The factory line's dashboard: admin-only, like the /admin/production/ reads behind it.
  {
    path: 'production',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionLinePage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/devices',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionDevicesPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/devices/:ficr',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionDevicePage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/chargers',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionChargersPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/chargers/:ficr',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionChargerPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/sets',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionSetsPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/quality',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionQualityPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/benches',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionBenchesPage />
      </RoleRoute>
    ),
  },
  {
    path: 'production/about',
    element: (
      <RoleRoute allow={canViewProduction}>
        <ProductionAboutPage />
      </RoleRoute>
    ),
  },
  { path: ':assetType', element: <AssetPage /> },
];
const isPanel = ({ path }) =>
  /^(coach\/|ops\/|support\/console$|profile$|production(\/|$))/.test(path);
const PanelLayoutWrapper = () => {
  const { pathname } = useLocation();
  return (
    <PanelLayout>
      <ErrorBoundary key={pathname}>
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </PanelLayout>
  );
};

export const dashboardRoutes = [
  {
    path: 'dashboard/insights',
    element: (
      <ProtectedRoute>
        <InsightsLayoutWrapper />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <InsightsPage /> },
      { path: ':customerId', element: <InsightsPage /> },
    ],
  },
  {
    path: 'dashboard',
    element: (
      <ProtectedRoute>
        <Outlet />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { element: <PanelLayoutWrapper />, children: pageRoutes.filter(isPanel) },
      {
        element: (
          <RoleRoute allow={canAccessAdminShell}>
            <DashboardLayoutWrapper />
          </RoleRoute>
        ),
        children: pageRoutes.filter((route) => !isPanel(route)),
      },
      { path: ':assetType', element: <AssetPage /> },
    ],
  },
];
